import {
  ProgressFetchTimeseriesDatapointsTool,
  RenderFetchTimeseriesDatapointsTool,
} from './FetchTimeseriesDatapointsTool.render.tsx';
import { extractExternalIdsFromRows } from './helpers.ts';
import {
  type FetchTimeseriesDatapointsInput,
  FetchTimeseriesDatapointsInputSchema,
  type FetchTimeseriesDatapointsResult,
} from './schema.ts';
import {
  fetchTimeseriesStep,
  TIMESERIES_TIMESTAMP_COLUMN,
} from '#plugin-chat/lib/data-pipeline/steps/fetch-timeseries.ts';
import { ToolGuidance } from '#plugin-chat/lib/instructions/components.tsx';
import { createTool } from '#plugin-chat/lib/tool/create-tool.tsx';
import type { ProducerToolConfig } from '#plugin-chat/lib/tool/create-tool.types.ts';
import { InvalidSpecError } from '#plugin-chat/lib/tool/errors/index.ts';

/**
 * LLM tool for fetching CDF timeseries datapoints with natural-language time
 * ranges. Supports both direct externalIds and batch fetching from a stored
 * knowledge-graph dataset.
 *
 * All CDF interaction, batching, caching, granularity computation, and
 * chart-format transformation live in {@link fetchTimeseriesStep}. The tool
 * layer only resolves `inputDataset` → externalIds against the chat-history
 * store (inside `produceDataset`) and formats the LLM-facing result envelope.
 *
 * Producer tool: the framework calls `produceDataset` first, stages the
 * result under `args.outputDataset`, and passes it into `execute`. Recovery
 * post-reload re-runs `produceDataset`, cascading into upstream recovery
 * for the `inputDataset` dataset if needed.
 */
export const FetchTimeseriesDatapointsTool = createTool<
  object,
  FetchTimeseriesDatapointsInput,
  FetchTimeseriesDatapointsResult
>('getDatapoints', () => ({
  instructions: (
    <ToolGuidance
      tool={FetchTimeseriesDatapointsTool}
      whenNotToUse={[
        'For instance metadata (use queryKnowledgeGraph)',
        'For current/latest values only (read `<ts-col>.latestDatapoint.value` on rows produced by queryKnowledgeGraph)',
        'For per-instance scalars or aggregates (latest value, monthly average, sum-over-window per row) — use `transformDataset` with its timeseries derive functions (see its "Derive expressions" instructions for the full set). Reach for `getDatapoints` only when the consumer needs the wide chart shape (one row per timestamp, one column per series).',
        'When data is already in stored outputs (use transformDataset)',
      ]}
      whenToUse={[
        'User wants a wide-format dataset for a time-axis chart — one row per timestamp, one column per series — typically consumed directly by `showDashboard` line/area/candlestick charts (omit `xAxis` to use the default `timestamp` column).',
        'Bulk fetching multiple series with a shared time window + aggregate when the consumer is a chart that needs the wide shape directly. For per-instance derived scalars, use `transformDataset` derives instead.',
        'Long historical windows or fine granularities where the up-front bulk fetch is preferable to expression-time fetching.',
      ]}
    >
      <p>
        Call {FetchTimeseriesDatapointsTool.mention} after querying a detail/agent view when the user asks about trends,
        history, charts, comparisons over time, or any temporal analysis.
      </p>

      <header>Output format</header>
      <p>
        Returns a wide-format Dataset with one <code>{TIMESERIES_TIMESTAMP_COLUMN}</code> column (ISO-8601 string) plus
        one numeric column per requested series. Series column names are the resolved timeseries externalIds (e.g.
        <code>ME010-S:WellOutputs:priorityOilRateStd</code>) — NOT the original agent view property names (e.g.
        <code>priorityOilRateStd</code>). When rendering a chart with <code>showDashboard</code>, omit
        <code>xAxis</code> entirely — it defaults to <code>{TIMESERIES_TIMESTAMP_COLUMN}</code> and matches this
        dataset's time column. Only set <code>xAxis</code> explicitly if pointing at a non-timeseries dataset with a
        different time column.
      </p>
    </ToolGuidance>
  ),
  config: {
    parameters: FetchTimeseriesDatapointsInputSchema,
    render: RenderFetchTimeseriesDatapointsTool,
    progress: ProgressFetchTimeseriesDatapointsTool,
    description: <p>Fetch timeseries datapoints from CDF for charting and temporal analysis.</p>,
    // produceDataset owns externalIds resolution + step invocation. Recovery
    // uses the same path, including the cascade via `ctx.store.get(inputDataset)`
    // which re-enters the chat-history store's recovery lookup for the upstream
    // producer (queryKnowledgeGraph, transformDataset, …).
    produceDataset: async (args, ctx) => {
      const { inputDataset, columns, externalIds: directExternalIds = [], startTime, endTime } = args;
      const aggregate = args.aggregate === 'none' ? undefined : args.aggregate;

      // Resolve inputDataset → externalIds by extracting them from the named
      // columns of the stored dataset's rows. Each column may hold either an
      // `EnrichedTimeseriesRef` object (the post-enrichment shape produced by
      // queryKnowledgeGraph) or a bare externalId string (e.g. a dotted-path
      // column materialised by transformDataset). `extractExternalIdsFromRows`
      // accepts both shapes — see helpers.ts.
      //
      // `ctx.resolve.byName` throws UnknownOutputDatasetError (a
      // ToolExecutionError subclass) when the inputDataset is unknown — the
      // framework translates it to the structured LLM envelope.
      const sourceDataset = inputDataset ? await ctx.resolve.byName(inputDataset) : undefined;
      const sourceOutputExternalIds = sourceDataset
        ? extractExternalIdsFromRows(sourceDataset.rows, columns ?? [])
        : [];

      const externalIds = [...new Set([...directExternalIds, ...sourceOutputExternalIds])];

      if (externalIds.length === 0) {
        throw new InvalidSpecError(
          'No external IDs provided — supply `externalIds` directly, or set `inputDataset` + `columns` to extract them from a stored dataset.',
          {
            suggestions: [
              'Pass `externalIds` directly',
              'Provide `inputDataset` and `columns` to pull externalIds from a stored dataset (from queryKnowledgeGraph, transformDataset, etc.)',
            ],
          },
        );
      }

      return fetchTimeseriesStep.execute(
        {
          externalIds,
          startTime,
          endTime,
          aggregate,
          granularity: args.granularity,
        },
        undefined,
        ctx,
      );
    },
    execute: ({ args, dataset }) => {
      const datapointCount = dataset.rows.length;
      const seriesKeys = dataset.columns.map((c) => c.name).filter((n) => n !== TIMESERIES_TIMESTAMP_COLUMN);
      const seriesCount = seriesKeys.length;
      const startDate = new Date(args.startTime).toLocaleDateString();
      const endDate = new Date(args.endTime).toLocaleDateString();
      const sourceInfo = args.inputDataset ? ` from '${args.inputDataset}'` : '';
      const columnInfo = args.columns ? ` (${args.columns.length} columns)` : '';
      const columnList = seriesKeys.map((k) => `  - ${k}`).join('\n');

      return {
        success: true as const,
        outputDataset: args.outputDataset,
        message: `Fetched ${datapointCount} datapoints for ${seriesCount} timeseries${sourceInfo}${columnInfo} from ${startDate} to ${endDate}.

Time column: \`${TIMESERIES_TIMESTAMP_COLUMN}\` (matches the default xAxis of \`showDashboard\` charts — omit \`xAxis\` to use it).
Series columns:\n${columnList}

Dataset stored with key '${args.outputDataset}'. Provide '${args.outputDataset}' as the \`inputDataset\` parameter when referencing this dataset in subsequent tool calls.`,
      };
    },
  } satisfies ProducerToolConfig<FetchTimeseriesDatapointsInput, FetchTimeseriesDatapointsResult>,
}));
